import { ArrowCircleUp, CaretLeft, CheckCircle } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const COMPLIANCE_HOME = "/home/settings/compliance";

const tiers: {
  tier: number;
  dailyLimit: string;
  loanLimit: string;
  requirements: string[];
  upgradePath?: string;
}[] = [
  {
    tier: 1,
    dailyLimit: "₦50,000",
    loanLimit: "₦50,000",
    requirements: [
      "Full name",
      "Gender",
      "Date of birth",
      "Address",
      "Mobile number",
    ],
  },
  {
    tier: 2,
    dailyLimit: "₦100,000",
    loanLimit: "₦100,000",
    requirements: [
      "Employment information",
      "Bank account details",
    ],
    upgradePath: "/home/settings/compliance/upgrade-tier-2",
  },
  {
    tier: 3,
    dailyLimit: "₦500,000",
    loanLimit: "₦500,000",
    requirements: [
      "Proof of address (recent utility bill)",
      "Government-issued ID (International Passport, Voter’s Card, or National ID)",
      "Additional checks may include liveness or passport-style photo where required",
    ],
    upgradePath: "/home/settings/compliance/upgrade-tier-3",
  },
];

const KYC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // const kycLevel = parseInt(
  //   user?.wallet?.tier ?? user?.compliance?.kyc_level ?? "1", 10
  // );
  const kycLevel = parseInt(
		user?.compliance?.kyc_level ?? user?.wallet?.tier ?? "1",
		10,
  );

  const goBackToCompliance = () => {
    navigate(COMPLIANCE_HOME);
  };

  return (
    <div className="bg-gray-50 px-5 md:px-10 py-8 flex items-center justify-center">
      <div className="flex flex-col gap-6 max-w-3xl w-full">
        <header className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-gray-900">KYC tiers &amp; limits</h1>
          <p className="text-sm text-gray-600">
            Daily and loan limits depend on your tier.{" "}
            <Link
              to={COMPLIANCE_HOME}
              className="font-medium text-deepBlue hover:underline"
            >
              Complete tasks on Compliance
            </Link>{" "}
            to upgrade.
          </p>
          {/* <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm pt-1">
            <Link
              to={`${COMPLIANCE_HOME}/utility-bill`}
              className="font-medium text-deepBlue hover:underline"
            >
              Upload utility bill
            </Link>
            <span className="text-gray-300 hidden sm:inline" aria-hidden>
              |
            </span>
            <Link
              to={COMPLIANCE_HOME}
              className="font-medium text-deepBlue hover:underline"
            >
              All compliance tasks
            </Link>
          </div> */}
        </header>

        <div className="bg-white rounded-xl w-full shadow-sm border border-gray-100 flex flex-col gap-5 p-5 md:p-6">
          <button
            type="button"
            onClick={goBackToCompliance}
            className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deepBlue/25"
          >
            <CaretLeft weight="bold" />
            <span>Back</span>
          </button>

          {tiers.map((t) => {
            const isCompleted = kycLevel >= t.tier;
            const isCurrent = kycLevel === t.tier;
            const needsUpgrade = kycLevel < t.tier;

            const heading = isCurrent
              ? "Current KYC level"
              : isCompleted
              ? `Tier ${t.tier} completed`
              : `Upgrade to Tier ${t.tier}`;

            const cardClass =
              "flex flex-col gap-4 rounded-xl border border-gray-200 p-4 md:p-5 bg-gray-50/50 transition-colors";

            const inner = (
              <>
                <div className="flex gap-4 items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-semibold text-gray-900">
                      {heading}
                    </div>
                    {needsUpgrade && (
                      <p className="text-xs text-gray-500 mt-1">
                        Tap this card to open Compliance and complete the
                        required tasks.
                      </p>
                    )}
                  </div>
                  {isCompleted ? (
                    <CheckCircle
                      size={32}
                      className="text-green-600 shrink-0"
                      weight="fill"
                      aria-hidden
                    />
                  ) : (
                    <ArrowCircleUp
                      size={32}
                      className="text-amber-500 shrink-0"
                      weight="fill"
                      aria-hidden
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-800">
                  <div className="rounded-lg bg-white border border-gray-100 px-3 py-2.5">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Daily limit
                    </div>
                    <div className="font-semibold text-gray-900 mt-0.5">
                      {t.dailyLimit}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white border border-gray-100 px-3 py-2.5">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Loan limit
                    </div>
                    <div className="font-semibold text-gray-900 mt-0.5">
                      {t.loanLimit}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Requirements
                  </div>
                  <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600 pl-0.5">
                    {t.requirements.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              </>
            );

            if (needsUpgrade) {
              return (
                <Link
                  key={t.tier}
                  to={t.upgradePath ?? COMPLIANCE_HOME}
                  className={`${cardClass} hover:bg-amber-50/40 hover:border-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deepBlue/25`}
                >
                  {inner}
                </Link>
              );
            }

            return (
              <div key={t.tier} className={cardClass}>
                {inner}
              </div>
            );
          })}

          <div className="pt-4 border-t border-gray-200 flex flex-col gap-2">
            <div className="font-medium text-gray-900">Continue verification</div>
            <p className="text-sm text-gray-600">
              Jump to a task or return to the full compliance checklist.
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-2 text-sm">
              {/* <Link
                to={`${COMPLIANCE_HOME}/utility-bill`}
                className="font-medium text-deepBlue hover:underline"
              >
                Utility bill
              </Link> */}
              <Link
                to={`${COMPLIANCE_HOME}/employment-details`}
                className="font-medium text-deepBlue hover:underline"
              >
                Employment details
              </Link>
              {/* <Link
                to={`${COMPLIANCE_HOME}/id-verification`}
                className="font-medium text-deepBlue hover:underline"
              >
                ID verification
              </Link> */}
              {/* <Link
                to={COMPLIANCE_HOME}
                className="font-medium text-deepBlue hover:underline"
              >
                All tasks
              </Link> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYC;
